import { useState, useEffect, useCallback, useRef } from 'react';
import {
    getLessonPlans,
    getPlanDetail,
    createDraftPlan,
    updatePlan,
    deletePlan
} from '../lib/api';
// import { debounce } from 'lodash'; // 如果不用可以移除

// 🔥 FIX: Field name aligned with Backend (content_markdown)
export interface Plan {
    id: number;
    lesson_id: number;
    version_number: string;
    content_markdown: string; // ✅ Correct field name
    is_final: boolean;
    created_at: string;
    updated_at: string;
    created_by?: number; 
    created_by_name?: string; 
    created_by_username?: string; 
}

export const usePlanVersionManager = (lessonId: number, artifactId?: number) => {
    const [plans, setPlans] = useState<Plan[]>([]);
    const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
    const [localContent, setLocalContent] = useState<string>("");
    const [originalContent, setOriginalContent] = useState<string>(""); 
    const [isContentChanged, setIsContentChanged] = useState<boolean>(false); 
    const [isSyncing, setIsSyncing] = useState(false);
    
    // 💡 核心修复 1：新增同步 Ref 跟踪器，防止闭包陷阱和 React 异步 setState 带来的时间差
    const localContentRef = useRef<string>("");
    const currentPlanRef = useRef<Plan | null>(null);
    
    // 更新ref，确保它始终指向最新的 currentPlan
    useEffect(() => {
        currentPlanRef.current = currentPlan;
    }, [currentPlan]);

    const mergePlanIntoList = useCallback((detail: Plan) => {
        setPlans(prev => {
            const exists = prev.some(p => p.id === detail.id);
            const next = exists
                ? prev.map(p => p.id === detail.id ? { ...p, ...detail } : p)
                : [detail, ...prev];

            return [...next].sort((a, b) => parseFloat(b.version_number) - parseFloat(a.version_number));
        });
    }, []);

    // 1. 初始化：加载列表
    const loadPlans = useCallback(async () => {
        if (!lessonId) return;
        try {
            const response = await getLessonPlans(lessonId);
            const list: Plan[] = response.data;

            const sorted = list.sort((a: Plan, b: Plan) =>
                parseFloat(b.version_number) - parseFloat(a.version_number)
            );
            setPlans(sorted);
            
            if (sorted.length > 0) {
                if (artifactId && sorted.some(p => p.id === artifactId)) {
                    selectPlan(artifactId);
                }
                else if (!currentPlanRef.current) {
                    selectPlan(sorted[0].id);
                }
            }
        } catch (err) {
            console.error("Failed to load plans", err);
        }
    }, [lessonId, artifactId]);

    // 2. 选中/切换版本
    const selectPlan = async (planId: number) => {
        setIsSyncing(true);
        try {
            const response = await getPlanDetail(planId);
            const detail: Plan = response.data;
            const content = detail.content_markdown || "";

            setCurrentPlan(detail);
            mergePlanIntoList(detail);
            setLocalContent(content);
            localContentRef.current = content; // 💡 同步更新 Ref
            setOriginalContent(content); 
            setIsContentChanged(false); 
        } finally {
            setIsSyncing(false);
        }
    };

    const refreshCurrentPlan = useCallback(async (planId?: number) => {
        const targetPlanId = planId ?? currentPlanRef.current?.id;
        if (!targetPlanId) return;
        await selectPlan(targetPlanId);
    }, []);

    // 3. 创建新版本
    const createNewVersion = async () => {
        if (!lessonId) return;
        setIsSyncing(true);
        try {
            const response = await createDraftPlan(lessonId);
            const newPlan: Plan = response.data;

            await loadPlans();
            await selectPlan(newPlan.id);
        } finally {
            setIsSyncing(false);
        }
    };

    // 4. 内容变更处理 - 💡 核心修复 2：同时更新 State 和 Ref
    const handleContentChange = useCallback((newContent: string) => {
        setLocalContent(newContent);
        localContentRef.current = newContent; // 保证后续同步读取的绝对最新

        const hasChanged = newContent !== originalContent;
        setIsContentChanged(hasChanged);
        
        if (hasChanged && currentPlanRef.current && currentPlanRef.current.is_final) {
            console.log("📝 Detected changes, marking as Draft...");
            setCurrentPlan(prev => prev ? { ...prev, is_final: false } : null);
            setPlans(prev => prev.map(p => p.id === currentPlanRef.current?.id ? { ...p, is_final: false } : p));
        }
    }, [originalContent]);

    // 💡 核心修复 3：新增专门的追加方法，彻底解决闭包旧值问题
    const appendContent = useCallback((addedText: string) => {
        const currentText = localContentRef.current;
        const newContent = currentText ? currentText + '\n\n' + addedText : addedText;
        handleContentChange(newContent);
        return newContent;
    }, [handleContentChange]);

    // 5. 手动保存 (定稿) - 💡 核心修复 4：直接从 Ref 读取，无视 React 渲染延迟
    const saveCurrentPlan = async () => {
        const targetPlan = currentPlanRef.current;
        if (!targetPlan) return;
        setIsSyncing(true);
        try {
            const contentToSave = localContentRef.current; // 绝对最新的内容，包含刚追加的 P5
            const hasContent = contentToSave.trim() !== '';
            const response = await updatePlan(targetPlan.id, {
                content_markdown: contentToSave,
                is_final: hasContent
            });
            const updated: Plan = response.data;

            setCurrentPlan(updated);
            setPlans((prev: Plan[]) => prev.map(p => p.id === updated.id ? updated : p));
            
            // 更新后续状态
            setLocalContent(updated.content_markdown || "");
            localContentRef.current = updated.content_markdown || "";
            setOriginalContent(updated.content_markdown || "");
            setIsContentChanged(false);
            console.log(hasContent ? "✅ Saved and marked as Final." : "✅ Saved and marked as Draft (empty content).");
        } finally {
            setIsSyncing(false);
        }
    };

    // 6. 删除教案
    const deleteCurrentPlan = async (planId: number) => {
        if (!planId) return;
        setIsSyncing(true);
        try {
            await deletePlan(planId);
            console.log("🗑️ Plan deleted successfully.");
            
            setCurrentPlan(null);
            setLocalContent("");
            localContentRef.current = ""; // 清理 ref
            
            await loadPlans();
        } catch (error) {
            console.error("Failed to delete plan:", error);
            throw error;
        } finally {
            setIsSyncing(false);
        }
    };

    // Initial Load
    useEffect(() => { loadPlans(); }, [loadPlans]);

    return {
        plans,
        currentPlan,
        localContent,
        isContentChanged,
        isSyncing,
        actions: {
            selectPlan,
            refreshCurrentPlan,
            createNewVersion,
            updateContent: handleContentChange,
            appendContent, // ✅ 导出新的追加方法
            savePlan: saveCurrentPlan,
            deletePlan: deleteCurrentPlan
        }
    };
};
